import {
    collection,
    doc,
    getDoc,
    getDocs,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Employee, EmployeeStatus } from '../types/hr';

const toDate = (value: unknown): Date | undefined => {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    if (typeof (value as { toDate?: unknown }).toDate === 'function') {
        return (value as { toDate: () => Date }).toDate();
    }
    return undefined;
};

const mapEmployeeStatus = (value: unknown): EmployeeStatus => {
    if (value === 'active' || value === 'inactive' || value === 'terminated') return value;
    return 'active';
};

const mapEmployee = (id: string, data: Record<string, unknown>): Employee => ({
    id,
    uid: typeof data.uid === 'string' ? data.uid : undefined,
    employeeId: typeof data.employeeId === 'string' ? data.employeeId : '',
    firstName: typeof data.firstName === 'string' ? data.firstName : '',
    surname: typeof data.surname === 'string' ? data.surname : '',
    email: typeof data.email === 'string' ? data.email : '',
    department: typeof data.department === 'string' ? data.department : '',
    jobTitle: typeof data.jobTitle === 'string' ? data.jobTitle : '',
    startDate: toDate(data.startDate),
    status: mapEmployeeStatus(data.status),
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
});

export const fetchEmployees = async (): Promise<Employee[]> => {
    const q = query(collection(db, 'employees'), orderBy('surname', 'asc'));
    const snap = await getDocs(q);
    return snap.docs.map((d) => mapEmployee(d.id, d.data() as Record<string, unknown>));
};

export const fetchEmployeeById = async (id: string): Promise<Employee | null> => {
    const snap = await getDoc(doc(db, 'employees', id));
    if (!snap.exists()) return null;
    return mapEmployee(snap.id, snap.data() as Record<string, unknown>);
};

export const updateEmployee = async (
    id: string,
    data: Partial<Omit<Employee, 'id' | 'createdAt'>>
): Promise<void> => {
    await updateDoc(doc(db, 'employees', id), {
        ...data,
        updatedAt: serverTimestamp(),
    });
};
